/**
 * QuePasa dashboard entrypoint.
 * Registers tabs, provider UI and realtime inbox-state syncing for the plugin.
 */

import type { App } from 'vue';
import type { I18n } from 'vue-i18n';
import { reactive } from 'vue';
import pluginMetadata from '../plugin.json';
import QuePasaChannel from './QuePasaChannel';
import QuePasaTab from './routes/dashboard/settings/inbox/settingsPage/QuePasaTab.vue';
import WebhooksTab from './routes/dashboard/settings/inbox/settingsPage/WebhooksTab.vue';
import SyncTab from './routes/dashboard/settings/inbox/settingsPage/SyncTab.vue';
import CSATEvaluationField from './components/CSATEvaluationField.vue';
import { isQuePasaInbox } from './helpers/inboxHelpers.js';
import QuePasaService from './services/QuePasaService.js';

// Import custom icon CSS
import '../assets/quepasa-icon.css';

interface SocketEventPayload<TData = any> {
  event: string;
  data: TData;
  connector: any;
  app: any;
  store: any;
}

interface PluginSystem {
  registerChannelType(channelType: string, i18nKey: string): void;
  registerTab(componentName: string, config: TabConfig): void;
  registerProvider(providerType: string, config: ProviderConfig): void;
  registerHook(hookName: string, callback: (data: any) => any): void;
  registerSocketEvent(
    eventName: string,
    callback: (payload: SocketEventPayload) => void
  ): void;
}

interface TabConfig {
  key: string;
  name: string;
  medium?: string;
  channelType?: string;
  provider?: string;
  condition?: (context: any) => boolean;
  component: any;
}

interface ProviderConfig {
  key: string;
  title: string;
  description: string;
  icon: string;
  component: any;
}

interface InstallContext {
  i18n?: I18n;
  PluginSystem?: PluginSystem;
}

const resolveQuePasaStateColor = (state?: string | null) => {
  const statusColors = pluginMetadata?.config?.STATUS_COLORS || {};
  const normalizedState = state ?? 'null';
  return statusColors[normalizedState] || statusColors.__default || null;
};

const automationExtension = {
  entityAttributes: {
    inbox: {
      inbox_id: {
        name: 'INBOX',
        inputType: 'multi_select',
        filterOperators: [
          { value: 'equal_to', label: 'Equal to' },
          { value: 'not_equal_to', label: 'Not equal to' },
        ],
      },
      quepasa_current_state: {
        name: 'QUEPASA_CURRENT_STATE',
        inputType: 'plain_text',
        filterOperators: [
          { value: 'equal_to', label: 'Equal to' },
          { value: 'not_equal_to', label: 'Not equal to' },
          { value: 'contains', label: 'Contains' },
          { value: 'does_not_contain', label: 'Does not contain' },
        ],
      },
      quepasa_previous_state: {
        name: 'QUEPASA_PREVIOUS_STATE',
        inputType: 'plain_text',
        filterOperators: [
          { value: 'equal_to', label: 'Equal to' },
          { value: 'not_equal_to', label: 'Not equal to' },
          { value: 'contains', label: 'Contains' },
          { value: 'does_not_contain', label: 'Does not contain' },
        ],
      },
    },
  },
  eventContexts: {
    inbox: {
      label: 'Inbox QuePasa',
      icon: '📡',
      events: ['quepasa_connection_state_changed'],
    },
  },
  eventEntities: {
    quepasa_connection_state_changed: ['inbox'],
  },
  actionsByEntity: {
    inbox: [
      'delay_next_action',
      'delay_next_action_range',
      'send_webhook_event',
    ],
  },
  automationRuleEvents: [
    {
      key: 'quepasa_connection_state_changed',
      value: 'QUEPASA_CONNECTION_STATE_CHANGED',
      context: 'inbox',
    },
  ],
};

// Keeps the global reactive runtime state in sync with server-side monitor events.
const syncInboxState = (payload: Record<string, any> = {}) => {
  const inboxId = payload?.inbox_id;
  const identifier = payload?.inbox_identifier;
  const currentState = payload?.current_state;
  const wid = payload?.wid;

  if ((window as any).quepasa?.inboxStates && inboxId && currentState) {
    (window as any).quepasa.inboxStates[inboxId] = currentState;
  }

  if ((window as any).quepasa?.widState && identifier) {
    (window as any).quepasa.widState[identifier] =
      typeof wid === 'string' && wid.trim().length > 0 ? wid : null;
  }

  window.dispatchEvent(
    new CustomEvent('quepasa:inbox-state-changed', { detail: payload })
  );
};

const registerAutomationExtension = (pluginSystem?: PluginSystem) => {
  if (typeof window === 'undefined') {
    return;
  }

  const automationApi = window.chatwoot?.plugins?.['automation-enhanced'];
  if (automationApi?.registerExtension) {
    automationApi.registerExtension(automationExtension);
  } else if (pluginSystem?.registerHook) {
    pluginSystem.registerHook('automation-enhanced:config', (config: any) => ({
      ...config,
      entityAttributes: {
        ...config.entityAttributes,
        ...automationExtension.entityAttributes,
      },
      eventContexts: {
        ...config.eventContexts,
        ...automationExtension.eventContexts,
      },
      eventEntities: {
        ...config.eventEntities,
        ...automationExtension.eventEntities,
      },
      actionsByEntity: {
        ...config.actionsByEntity,
        ...automationExtension.actionsByEntity,
      },
      automationRuleEvents: [
        ...config.automationRuleEvents,
        ...automationExtension.automationRuleEvents,
      ],
    }));
  }
};

export function install(app: App, { i18n, PluginSystem }: InstallContext = {}) {
  registerAutomationExtension(PluginSystem);

  // Initialize QuePasaService globally
  if (typeof window !== 'undefined') {
    if (!window.chatwoot) window.chatwoot = {};
    if (!window.chatwoot.plugins) window.chatwoot.plugins = {};
    if (!window.chatwoot.plugins['quepasa-inbox']) window.chatwoot.plugins['quepasa-inbox'] = {};

    window.chatwoot.plugins['quepasa-inbox'].config = pluginMetadata.config;

    // QuePasaService is already a singleton instance (not a class)
    window.chatwoot.plugins['quepasa-inbox'].service = QuePasaService;

    if (!window.quepasa) window.quepasa = {};
    window.quepasa.config = pluginMetadata.config;
    window.quepasa.service = QuePasaService;
    window.quepasa.current = () => QuePasaService.current();
    window.quepasa.getStateColor = (state?: string | null) =>
      resolveQuePasaStateColor(state);

    // Reactive WID state: keyed by inbox_identifier → wid string (or null)
    // Vue tracks reads of this inside computed tabs(), so tabs re-evaluate when wid loads
    if (!window.quepasa.widState) {
      window.quepasa.widState = reactive({} as Record<string, string | null>);
    }

    // Reactive inbox connection states: keyed by inbox.id (number) → state string
    // Updated by QuePasaService._fetchWidStates() and QuePasaTab.vue on health load.
    // Consumed by sidebar ChannelLeaf and settings Index transforms for visual indicators.
    if (!window.quepasa.inboxStates) {
      window.quepasa.inboxStates = reactive({} as Record<number, string>);
    }

    console.log('[QuePasa Service] Registered globally at:');
    console.log('  - window.chatwoot.plugins["quepasa-inbox"].config');
    console.log('  - window.chatwoot.plugins["quepasa-inbox"].service');
    console.log('  - window.quepasa.config');
    console.log('  - window.quepasa.service');
    console.log('  - window.quepasa.current()');
    
    // Dispatch ready event
    window.dispatchEvent(new CustomEvent('quepasa:ready', { 
      detail: { service: QuePasaService } 
    }));
    console.log('[QuePasa Service] ✅ Ready event dispatched');
  }

  // Register QuePasa components globally
  app.component('QuePasaTab', QuePasaTab);
  app.component('WebhooksTab', WebhooksTab);
  app.component('SyncTab', SyncTab);
  app.component('CSATEvaluationField', CSATEvaluationField);

  // Register global helper for inbox detection
  app.config.globalProperties.$isQuePasaInbox = isQuePasaInbox;

  // Event-based initialization: Subscribe to Vuex store events
  if (typeof window !== 'undefined') {
    // Listen for store ready event via Vuex subscription
    const setupInitialization = () => {
      const store = app.config?.globalProperties?.$store;
      if (!store) {
        console.log('[QuePasa Service] Store not available, retrying...');
        setTimeout(setupInitialization, 500);
        return;
      }

      // Expose store globally for service access
      window.__chatwoot_store = store;
      console.log('[QuePasa Service] Store available, subscribing to inbox events');
      
      // Subscribe to inbox-related mutations
      store.subscribe((mutation: any) => {
        if (mutation.type === 'inboxes/SET_INBOXES') {
          console.log('[QuePasa Service] Inboxes loaded, initializing service');
          QuePasaService.ensureInitialized(store);
        }
      });

      // Try immediate initialization if inboxes already loaded
      const inboxes = store.getters?.['inboxes/getInboxes'];
      if (inboxes && inboxes.length > 0) {
        console.log('[QuePasa Service] Inboxes already loaded, initializing now');
        QuePasaService.ensureInitialized(store);
      }
    };

    setupInitialization();
  }

  // Register QuePasa settings tab using new Plugin System
  if (PluginSystem) {
    PluginSystem.registerSocketEvent(
      'quepasa.inbox_state_changed',
      ({ data }) => syncInboxState(data)
    );

    // Register channel type for ChannelName component
    // QuePasa uses medium='quepasa' to differentiate from other API channels (like Twilio uses medium='whatsapp')
    PluginSystem.registerChannelType('Channel::Api:quepasa', 'QUEPASA');

    PluginSystem.registerTab('Settings', {
      key: 'quepasa',
      name: 'QuePasa',
      // Use condition with isQuePasaInbox helper for robust detection
      // (handles all payload shapes: medium, additional_attributes.provider, etc.)
      condition: (context: any) => {
        const inbox = context?.inbox;
        return isQuePasaInbox(inbox);
      },
      component: QuePasaTab,
    });

    PluginSystem.registerTab('Settings', {
      key: 'webhooks',
      name: 'Webhooks',
      condition: (context: any) => {
        const inbox = context?.inbox;
        return isQuePasaInbox(inbox);
      },
      component: WebhooksTab,
    });

    // Register Sync tab
    // Visibility is controlled here using plugin tab condition.
    // Rule: show only when QuePasa API confirms a WID (WhatsApp ID) was ever linked to this inbox.
    // Rationale: having inbox_identifier (the placeholder) in QuePasa is not enough —
    // the QR code must have been scanned at least once (wid must be non-empty).
    // window.quepasa.widState is a Vue reactive object; accessing it here inside tabs()
    // computed makes tabs() re-run automatically when the WID check completes.
    PluginSystem.registerTab('Settings', {
      key: 'sync',
      name: 'Sync',
      condition: context => {
        if (!isQuePasaInbox(context?.inbox)) return false;
        const inbox = context?.inbox;
        const identifier = inbox?.inbox_identifier;
        if (!identifier) return false;

        // Access reactive widState so Vue tracks it as a dependency of tabs() computed.
        // undefined = not yet fetched (tab hidden while loading)
        // null = fetched, no wid (tab hidden)
        // string = fetched, wid exists (tab visible)
        const widState = (window as any).quepasa?.widState;
        if (!widState) return false;

        const wid = widState[identifier];
        return typeof wid === 'string' && wid.trim().length > 0;
      },
      component: SyncTab,
    });

    PluginSystem.registerProvider('whatsapp', {
      key: 'quepasa',
      title: 'QuePasa',
      description: 'Self-hosted WhatsApp API',
      icon: 'i-woot-quepasa',
      component: QuePasaChannel,
    });
  } else {
    
    // Legacy fallback: usar window.chatwoot.plugins (criado pelo sistema de plugins)
    if (!window.chatwoot) {
      window.chatwoot = {};
    }
    if (!window.chatwoot.plugins) {
      window.chatwoot.plugins = {};
    }
    if (!window.chatwoot.plugins.whatsappProviders) {
      window.chatwoot.plugins.whatsappProviders = [];
    }
    
    window.chatwoot.plugins.whatsappProviders.push({
      key: 'quepasa',
      title: 'QuePasa',
      description: 'Self-hosted WhatsApp API',
      icon: 'i-woot-quepasa',
      component: QuePasaChannel,
    });
  }
}

export default {
  install,
};
